import type { QuotaResult, ZaiQuotaData } from '../types.ts';
import { getZaiApiKey } from '../auth.ts';

interface ZaiQuotaResponse {
  used?: number;
  total?: number;
  limit?: number;
  data?: {
    used?: number;
    total?: number;
    limit?: number;
  };
}

const toNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  return value;
};

const firstNumber = (values: Array<number | null>): number => {
  for (const value of values) {
    if (value !== null) {
      return value;
    }
  }

  return 0;
};

const formatTokens = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }

  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }

  return String(value);
};

export const fetchZaiQuota = async (): Promise<QuotaResult> => {
  const apiKey = await getZaiApiKey();

  if (!apiKey) {
    return {
      provider: 'zai',
      ok: false,
      configured: false,
      summary: 'Not configured - no API key found',
      data: null,
    };
  }

  try {
    const response = await fetch('https://api.z.ai/api/monitor/usage/quota/limit', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return {
        provider: 'zai',
        ok: false,
        configured: true,
        summary: `API error: ${response.status}`,
        error: await response.text(),
        data: null,
      };
    }

    const payload = (await response.json()) as ZaiQuotaResponse;
    const usedTokens = firstNumber([toNumber(payload.used), toNumber(payload.data?.used)]);
    const totalTokens = firstNumber([
      toNumber(payload.total),
      toNumber(payload.limit),
      toNumber(payload.data?.total),
      toNumber(payload.data?.limit),
    ]);

    const usedPercent = totalTokens > 0 ? Math.round((usedTokens / totalTokens) * 100) : 0;
    const remainingPercent = Math.max(0, 100 - usedPercent);

    const data: ZaiQuotaData = {
      usedTokens,
      totalTokens,
      usedPercent,
      remainingPercent,
      planTier: null,
    };

    return {
      provider: 'zai',
      ok: true,
      configured: true,
      summary: `${data.remainingPercent}% remaining (${formatTokens(usedTokens)} / ${formatTokens(
        totalTokens
      )})`,
      data,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      provider: 'zai',
      ok: false,
      configured: true,
      summary: `Request failed: ${message}`,
      error: message,
      data: null,
    };
  }
};
